/**
 * PendingStepBannerPure
 *
 * Pure presentational component for pending step banner.
 * No server dependencies - all data and callbacks passed via props.
 */
import { useEffect, useRef, useState } from 'react';

import { cn } from '../cn';
import { Button } from '../Input2';
import { Alerts, Arrows, Editor, Time } from '../StrokeIcons';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { CopyFeedbackButton } from './CopyFeedbackButton';
import { SendOutcomeNoticePure } from './SendOutcomeNoticePure';

// ============================================================================
// Types
// ============================================================================

/**
 * The queued send did not go through. Present → the card shows the Failed variant: the full text
 * (the rep may want to reuse it), the error in plain words, and Retry / Edit / Copy / Delete.
 */
export type PendingStepFailed = {
  /** Plain-language reason, or null when the provider gave none. */
  errorMessage: string | null;
  /** The full message text (not a preview) — what Retry would send again. */
  body: string | null;
  /** ISO timestamp of the failed attempt, or null when unknown. */
  failedAt: string | null;
  onRetry: () => void;
  isRetrying?: boolean;
  /** Open the text in the composer. Absent → no Edit button. */
  onEdit?: () => void;
  /** An editor is already open with other text: Edit asks before replacing it. */
  editNeedsConfirm?: boolean;
  /** Put the text on the clipboard. Absent → no Copy button. */
  onCopy?: () => Promise<void> | void;
  onDelete: () => void;
  isDeleting?: boolean;
};

export type PendingStepBannerPureProps = {
  /** Stable identity of the pending step. When this changes, the banner briefly flashes
      to draw the user's eye to "this is the queued send." Pass the ai_step / step ID. */
  stepId?: string | null;
  bodyPreview: string | null;
  type: string;
  scheduledFor: string | null;
  /** ai_step.status — used to distinguish "approved & scheduled to send" from
      "drafted, waiting for the user to review." Only 'approved' means the user has
      authorized the send; everything else (not_started, researched, waiting_for_user)
      still needs their review. */
  status?: string | null;
  onDelete: () => void;
  isDeleting?: boolean;
  /** Send this step right away, skipping the LinkedIn schedule window once. Only offered for
      `approved` steps — a drafted step still needs review. Absent → no button. */
  onSendNow?: () => void;
  isSendingNow?: boolean;
  /** Present → the Failed variant replaces the scheduled/drafted card entirely. */
  failed?: PendingStepFailed | null;
};

// ============================================================================
// Helper Functions
// ============================================================================

function formatFailedAt(failedAt: string | null): string | null {
  if (!failedAt) return null;
  const date = new Date(failedAt);
  if (Number.isNaN(date.getTime())) return null;
  const timeStr = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) return `Failed at ${timeStr}`;
  const dateStr = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  return `Failed ${dateStr} at ${timeStr}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatScheduledTime(scheduledFor: string | null): string {
  if (!scheduledFor) return 'soon';

  const date = new Date(scheduledFor);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMs < 0) return 'soon';
  if (diffMins < 60) return `in ${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
  // Within 24h: include the actual clock time so the user can see WHEN, not just "in 4 hours."
  if (diffHours < 24) {
    const timeStr = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `in ${diffHours} hour${diffHours !== 1 ? 's' : ''} (${timeStr})`;
  }

  // Beyond 24h: show the day + time so it's unambiguous.
  const dateStr = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const timeStr = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${dateStr} at ${timeStr}`;
}

function getStepTypeLabel(type: string): string {
  switch (type) {
    case 'message':
      return 'message';
    case 'inmail':
      return 'InMail';
    case 'connect':
      return 'connection request';
    case 'video_note':
      return 'video note';
    case 'audio':
    case 'voice_note':
      return 'voice note';
    case 'email':
      return 'email';
    default:
      return type;
  }
}

// ============================================================================
// Component
// ============================================================================

export function PendingStepBannerPure({
  stepId,
  bodyPreview,
  type,
  scheduledFor,
  status,
  onDelete,
  isDeleting,
  onSendNow,
  isSendingNow,
  failed,
}: PendingStepBannerPureProps) {
  const scheduledTimeLabel = formatScheduledTime(scheduledFor);
  const stepTypeLabel = getStepTypeLabel(type);
  const needsReview = status != null && status !== 'approved';

  // Flash the banner + scroll into view when a new step appears, so the user sees
  // confirmation that their generate-and-approve flow actually queued a send. Without
  // this, users wonder whether the action took effect and re-trigger generation.
  const ref = useRef<HTMLDivElement>(null);
  const [flashing, setFlashing] = useState(false);
  const prevStepId = useRef<string | null | undefined>(stepId);

  useEffect(() => {
    if (stepId == null) {
      prevStepId.current = stepId;
      return;
    }
    if (prevStepId.current === stepId) return;
    prevStepId.current = stepId;
    setFlashing(true);
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const t = setTimeout(() => setFlashing(false), 1500);
    return () => clearTimeout(t);
  }, [stepId]);

  if (failed != null) {
    const failedAtLabel = formatFailedAt(failed.failedAt);
    const busy = failed.isRetrying === true || failed.isDeleting === true;
    return (
      <div
        ref={ref}
        data-testid="pending-step-failed"
        className={cn(
          'flex flex-col gap-2 rounded-lg border border-red-200 bg-white p-3 transition-shadow dark:border-red-500/30 dark:bg-gray-900',
          flashing && 'shadow-[0_0_0_3px_rgba(239,68,68,0.35)]',
        )}>
        <div className="flex items-center gap-2">
          <Button readOnly shape="pill" color="red" tone="soft" size="2xs" data-testid="pending-step-failed-pill">
            Failed
          </Button>
          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{capitalize(stepTypeLabel)}</span>
        </div>
        {failed.body && (
          <div
            data-testid="pending-step-failed-body"
            className="text-sm text-gray-700 [&_p]:inline dark:text-gray-300"
            dangerouslySetInnerHTML={{ __html: `"${sanitizeHtml(failed.body)}"` }}
          />
        )}
        <SendOutcomeNoticePure
          tone="failed"
          title="Not sent."
          body={failed.errorMessage ?? 'LinkedIn gave no reason. Retry, or edit the text and send it again.'}
        />
        <div className="flex flex-wrap items-center gap-2">
          {failedAtLabel != null && (
            <>
              <Time.Clock className="size-3.5 text-gray-500 dark:text-gray-400" />
              <span className="text-xs text-gray-600 dark:text-gray-400" data-testid="pending-step-failed-at">
                {failedAtLabel}
              </span>
            </>
          )}
          <div className="flex-1" />
          <Button
            size="xs"
            color="red"
            icon={<Arrows.RefreshCw04 />}
            status={failed.isRetrying ? 'updating' : undefined}
            onClick={failed.onRetry}
            disabled={busy}
            data-testid="pending-step-retry">
            {failed.isRetrying ? 'Retrying…' : 'Retry'}
          </Button>
          {failed.onEdit != null && (
            <Button
              size="xs"
              tone="outline"
              icon={<Editor.Edit03 />}
              onClick={failed.onEdit}
              confirm={
                failed.editNeedsConfirm
                  ? {
                      title: 'Replace what you are writing?',
                      description: 'The text in the open editor will be replaced with this message.',
                      confirmLabel: 'Replace',
                    }
                  : undefined
              }
              disabled={busy}
              data-testid="pending-step-edit">
              Edit
            </Button>
          )}
          {failed.onCopy != null && <CopyFeedbackButton onCopy={failed.onCopy} data-test="pending-step-copy" />}
          <Button
            size="xs"
            color="red"
            tone="outline"
            onClick={failed.onDelete}
            disabled={busy}
            data-testid="pending-step-delete">
            Delete
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 transition-shadow dark:border-amber-800 dark:bg-amber-900/20',
        flashing && 'shadow-[0_0_0_3px_rgba(245,158,11,0.45)] dark:shadow-[0_0_0_3px_rgba(217,119,6,0.45)]',
      )}>
      <div className="flex items-start gap-2">
        <Alerts.AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-amber-800 dark:text-amber-200">
            {/* While a send-now is in flight the scheduled time is meaningless — it is going out
                right now — and repeating it reads as "nothing happened". */}
            {isSendingNow
              ? `Sending ${stepTypeLabel} now…`
              : needsReview
                ? `Drafted ${stepTypeLabel} — review to send`
                : `Scheduled ${stepTypeLabel} ${scheduledTimeLabel}`}
          </div>
          {bodyPreview && (
            <div
              className="mt-1 line-clamp-2 text-sm text-amber-700 [&_p]:inline dark:text-amber-300"
              dangerouslySetInnerHTML={{ __html: `"${sanitizeHtml(bodyPreview)}"` }}
            />
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Time.Clock className="size-3.5 text-amber-600 dark:text-amber-400" />
        <span className="text-xs text-amber-700 dark:text-amber-300">
          {isSendingNow
            ? 'Waiting for LinkedIn to confirm delivery'
            : needsReview
              ? 'Generating a new draft will replace this one'
              : 'Scheduling a new message will replace this one'}
        </span>
        <div className="flex-1" />
        {onSendNow != null && !needsReview && !isSendingNow && (
          <Button
            size="xs"
            color="green"
            onClick={onSendNow}
            disabled={isSendingNow || isDeleting}
            data-testid="pending-step-send-now"
            tooltip="Send right away instead of waiting for your LinkedIn hours">
            {isSendingNow ? 'Sending…' : 'Send now'}
          </Button>
        )}
        {!isSendingNow && (
          <Button size="xs" color="red" tone="soft" onClick={onDelete} disabled={isDeleting}>
            Delete
          </Button>
        )}
      </div>
    </div>
  );
}
